import { useState, useEffect, useCallback } from 'react';
import type { Submission, SubmissionIndexEntry } from '../types/submission';

interface UseSubmissionsReturn {
  submissions: SubmissionIndexEntry[];
  loading: boolean;
  error: string | null;
  submitRecording: (
    capsuleSlug: string,
    layerName: string,
    recordingUrl: string,
    note?: string
  ) => Promise<Submission>;
  refresh: () => void;
}

export function useSubmissions(): UseSubmissionsReturn {
  const [submissions, setSubmissions] = useState<SubmissionIndexEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function fetchSubmissions() {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch('/api/submissions/list', { credentials: 'include' });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Failed to fetch submissions (${res.status})`);
        }
        const data = await res.json();
        if (!cancelled) {
          setSubmissions(data.submissions || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load submissions');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchSubmissions();
    return () => { cancelled = true; };
  }, [refreshKey]);

  const submitRecording = useCallback(
    async (
      capsuleSlug: string,
      layerName: string,
      recordingUrl: string,
      note?: string
    ): Promise<Submission> => {
      const res = await fetch('/api/submissions/create', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capsuleSlug, layerName, recordingUrl, note }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit recording');
      }

      // Refresh the list
      setRefreshKey(k => k + 1);

      return data.submission;
    },
    []
  );

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  return { submissions, loading, error, submitRecording, refresh };
}
